import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventApi } from '../../../services/api';

const ScheduleTab = ({ tournament, matches, isOrganizer, canScore: canScoreProp, getRoundName, onOpenScheduleModal, onDeleteSchedule, onViewDeleted, onSelectWinner, onOpenScoreModal, filterEventId = 'all', onFilterChange }) => {
  const canScore = canScoreProp ?? isOrganizer; // fallback: umpires can score but not manage
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const setFilterEventId = onFilterChange || (() => {});

  useEffect(() => {
    if (!tournament?.id) return;
    eventApi.list(tournament.id)
      .then(res => setEvents(res.data || []))
      .catch(() => {});
  }, [tournament?.id]);

  // If the tournament uses events, exclude stale tournament-level matches (event_id = null)
  // that may exist from before the events system was introduced.
  const hasEventMatches = (matches || []).some(m => m.status !== 'deleted' && m.event_id != null);
  const visibleMatches = (matches || []).filter(m => !hasEventMatches || m.event_id != null);

  const hasAnyMatches = visibleMatches.length > 0;

  if (!hasAnyMatches) {
    return (
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>Tournament Schedule</h3>
        </div>
        <div className="empty-state">
          <p>Draw has not been generated yet.</p>
        </div>
      </div>
    );
  }

  const allActive = visibleMatches.filter(m => m.status !== 'deleted').sort((a, b) => {
    const dateA = a.scheduled_time ? new Date(a.scheduled_time).getTime() : null;
    const dateB = b.scheduled_time ? new Date(b.scheduled_time).getTime() : null;
    // Both have a time: sort chronologically, then court, then round, then match
    if (dateA !== null && dateB !== null) {
      if (dateA !== dateB) return dateA - dateB;
      if (a.court_number !== b.court_number) return (a.court_number || 99) - (b.court_number || 99);
      if (a.round_number !== b.round_number) return a.round_number - b.round_number;
      return a.match_number - b.match_number;
    }
    // Scheduled matches come before unscheduled
    if (dateA !== null) return -1;
    if (dateB !== null) return 1;
    // Neither scheduled: show early rounds first (Round 1 before Final), then match_number
    if (a.round_number !== b.round_number) return a.round_number - b.round_number;
    return a.match_number - b.match_number;
  });

  const hasDeletedSchedule = matches.some(m => m.status === 'deleted');
  const hasSchedule = allActive.some(m => m.scheduled_time);
  const hasDraw = allActive.length > 0;

  // Only show matches that have been explicitly scheduled
  const scheduledActive = allActive.filter(m => m.scheduled_time);

  const activeMatches = filterEventId === 'all'
    ? scheduledActive
    : scheduledActive.filter(m => m.event_id === filterEventId);

  // Events that have scheduled matches (for the filter dropdown)
  const eventsWithMatches = events.filter(ev => scheduledActive.some(m => m.event_id === ev.id));

  // Draw generated but no schedule yet — prompt to generate schedule
  if (hasDraw && !hasSchedule) {
    return (
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3>Tournament Schedule</h3>
          {hasDeletedSchedule && (
            <button onClick={onViewDeleted} className="btn btn-outline btn-sm">📋 Deleted Schedule</button>
          )}
        </div>
        <div className="empty-state">
          <p>Draw has been generated. Generate a schedule to assign court timings.</p>
        </div>
        {isOrganizer && (
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button onClick={onOpenScheduleModal} className="btn btn-primary">+ Generate Schedule</button>
          </div>
        )}
      </div>
    );
  }

  if (!hasDraw && !isOrganizer) {
    return (
      <div className="card">
        <h3>Tournament Schedule</h3>
        <div className="empty-state"><p>Schedule has not been created yet.</p></div>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Tournament Schedule</h3>
        {isOrganizer && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={onOpenScheduleModal} className="btn btn-primary btn-sm">
              {hasSchedule ? '🔄 Regenerate Schedule' : '+ Create Schedule'}
            </button>
            <button onClick={onDeleteSchedule} className="btn btn-outline btn-sm" title="Clear Schedule Timings">🗑️ Clear</button>
            <button onClick={onViewDeleted} className="btn btn-outline btn-sm">📋 Deleted</button>
          </div>
        )}
      </div>

      {/* Event filter tabs */}
      {eventsWithMatches.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '1rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '0.5rem' }}>
          <button
            onClick={() => setFilterEventId('all')}
            style={{
              padding: '0.3rem 0.9rem', borderRadius: '6px 6px 0 0', fontSize: '0.82rem',
              fontWeight: filterEventId === 'all' ? '700' : '500',
              background: filterEventId === 'all' ? 'var(--primary-color)' : 'transparent',
              color: filterEventId === 'all' ? 'white' : '#6b7280',
              border: filterEventId === 'all' ? '2px solid var(--primary-color)' : '2px solid transparent',
              borderBottom: 'none', cursor: 'pointer', marginBottom: '-2px',
            }}
          >
            All ({allActive.length})
          </button>
          {eventsWithMatches.map(ev => {
            const count = allActive.filter(m => m.event_id === ev.id).length;
            const active = filterEventId === ev.id;
            return (
              <button
                key={ev.id}
                onClick={() => setFilterEventId(ev.id)}
                style={{
                  padding: '0.3rem 0.9rem', borderRadius: '6px 6px 0 0', fontSize: '0.82rem',
                  fontWeight: active ? '700' : '500',
                  background: active ? 'var(--primary-color)' : 'transparent',
                  color: active ? 'white' : '#6b7280',
                  border: active ? '2px solid var(--primary-color)' : '2px solid transparent',
                  borderBottom: 'none', cursor: 'pointer', marginBottom: '-2px',
                  whiteSpace: 'nowrap',
                }}
              >
                {ev.name}{ev.age_category ? ` (${ev.age_category})` : ''} ({count})
              </button>
            );
          })}
        </div>
      )}

      {activeMatches.length === 0 ? (
        <div className="empty-state"><p>No matches for this event yet.</p></div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                {filterEventId === 'all' && eventsWithMatches.length > 1 && <th>Event</th>}
                <th>Match</th>
                <th>Round</th><th>Date</th><th>Time</th><th>Court</th>
                <th>Player 1</th><th>Player 2</th><th>Score</th><th>Winner</th>
              </tr>
            </thead>
            <tbody>
              {activeMatches.map((match, idx) => {
                const isBye = match.notes && match.notes.toUpperCase().includes('BYE');
                const dateObj = match.scheduled_time ? new Date(match.scheduled_time) : null;
                const dateStr = dateObj ? dateObj.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '-';
                const timeStr = dateObj ? dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-';
                // "Enter Score" — available to any scorer once both players are assigned and there's no winner yet
                const canEnterScore = canScore && !match.winner_id && match.player1_id && match.player2_id;
                // "Score Live" — requires a scheduled time (must know when/where the match is) and is today or already live
                const today = new Date(); today.setHours(0,0,0,0);
                const matchDay = dateObj ? new Date(dateObj) : null;
                if (matchDay) matchDay.setHours(0,0,0,0);
                const isToday = matchDay && matchDay.getTime() === today.getTime();
                const isDesktop = !!window.electronAPI;
                const canScoreLive = !isDesktop && canEnterScore && !!match.scheduled_time && (isToday || match.is_live);
                const eventName = filterEventId === 'all' && eventsWithMatches.length > 1
                  ? events.find(e => e.id === match.event_id)?.name
                  : null;

                return (
                  <tr key={match.id}>
                    <td style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{idx + 1}</td>
                    {filterEventId === 'all' && eventsWithMatches.length > 1 && (
                      <td style={{ fontSize: '0.8rem', color: '#374151', maxWidth: '140px' }}>
                        {eventName ? (
                          <span style={{ display: 'inline-block', padding: '1px 6px', background: '#CCFBF1', color: '#115E59', borderRadius: 999, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '130px' }}>
                            {eventName}
                          </span>
                        ) : '—'}
                      </td>
                    )}
                    <td><span style={{ fontWeight: '600', color: '#0D9488', fontSize: '0.85rem' }}>M{match.match_number}</span></td>
                    <td>{getRoundName(match.round_number, match.event_id)}</td>
                    <td>{dateStr}</td>
                    <td>{timeStr}</td>
                    <td>{match.court_number || '-'}</td>
                    <td style={{ fontWeight: match.winner_id === match.player1_id || match.player1_seed ? 'bold' : 'normal', color: match.winner_id === match.player1_id ? '#28a745' : 'inherit' }}>
                      {match.player1_display || match.player1_name || (isBye ? 'BYE' : 'TBD')}
                      {match.player1_seed ? <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.75rem', marginLeft: '3px' }}>[{match.player1_seed}]</span> : ''}
                    </td>
                    <td style={{ fontWeight: match.winner_id === match.player2_id || match.player2_seed ? 'bold' : 'normal', color: match.winner_id === match.player2_id ? '#28a745' : 'inherit' }}>
                      {match.player2_display || match.player2_name || (isBye ? 'BYE' : 'TBD')}
                      {match.player2_seed ? <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.75rem', marginLeft: '3px' }}>[{match.player2_seed}]</span> : ''}
                    </td>
                    <td>
                      {isBye ? '(BYE)' : (match.score ? (
                        (() => {
                          try {
                            // mysql2 returns JSON columns as objects; guard against both cases
                            const scoreData = typeof match.score === 'string' ? JSON.parse(match.score) : match.score;
                            if (scoreData.games === 'Walkover') return <span className="badge badge-warning">Walkover</span>;
                            const games = scoreData.games || '';
                            const pts = match.points_per_set || 21;
                            const cap = pts === 15 ? 21 : 30;
                            const isGP = (a, b) => Math.max(a, b) === cap && Math.abs(a - b) === 1;
                            const pairs = [
                              [parseInt(scoreData.game1?.player1)||0, parseInt(scoreData.game1?.player2)||0],
                              [parseInt(scoreData.game2?.player1)||0, parseInt(scoreData.game2?.player2)||0],
                              [parseInt(scoreData.game3?.player1)||0, parseInt(scoreData.game3?.player2)||0],
                            ].filter(([a, b]) => !(a === 0 && b === 0));
                            if (pairs.length === 0) return '-';
                            return (
                              <span>
                                {games} ({pairs.map(([a, b], i) => (
                                  <React.Fragment key={i}>
                                    {i > 0 && ', '}
                                    <span style={isGP(a, b) ? { color: '#b45309', fontWeight: '600' } : {}}>
                                      {a}-{b}{isGP(a, b) && <span title="Golden Point" style={{ marginLeft: '2px' }}>⚡</span>}
                                    </span>
                                  </React.Fragment>
                                ))})
                              </span>
                            );
                          } catch { return '-'; }
                        })()
                      ) : '-')}
                    </td>
                    <td>
                      {match.winner_id ? (
                        <span className="badge badge-success">{match.winner_name}</span>
                      ) : (
                        isBye && (match.player1_id || match.player2_id) ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <span className="badge badge-warning" style={{ fontSize: '0.7rem' }}>BYE (Pending)</span>
                            {isOrganizer && (
                              <button
                                className="btn btn-sm btn-primary"
                                style={{ fontSize: '0.65rem', padding: '0.1rem 0.3rem' }}
                                onClick={() => onSelectWinner(match.id, match.player1_id || match.player2_id)}
                              >
                                Advance Player
                              </button>
                            )}
                          </div>
                        ) : canEnterScore ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                            {canScoreLive && (
                              match.is_live ? (
                                <button
                                  className="btn btn-sm"
                                  style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem', background: '#22c55e', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}
                                  onClick={() => navigate(`/live-score/${match.id}`)}
                                >
                                  🔴 Resume Live
                                </button>
                              ) : (
                                <button
                                  className="btn btn-sm"
                                  style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem', background: '#0D9488', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700 }}
                                  onClick={() => navigate(`/live-score/${match.id}`)}
                                >
                                  ▶ Score Live
                                </button>
                              )
                            )}
                            <button className="btn btn-sm btn-outline" style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }} onClick={() => onOpenScoreModal(match)}>
                              Enter Score
                            </button>
                          </div>
                        ) : match.is_live ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', color: '#22c55e', fontWeight: 700 }}>
                            🔴 LIVE
                          </span>
                        ) : '-'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ScheduleTab;
